/**
 * Native exit-intent modal showing recently viewed products.
 *
 * Data source: localStorage key "recentlyViewedProducts"
 * Card rendering: /products/:handle?view=card-only (already supported by theme snippets)
 */
(() => {
  const THEME_CONFIG = window.BB_EXIT_RV_MODAL || {};
  const FREE_SHIPPING_THRESHOLD_CENTS = 12000;

  function resolveFreeShippingThresholdCents(value) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    return FREE_SHIPPING_THRESHOLD_CENTS;
  }

  const CONFIG = {
    enabled: THEME_CONFIG.enabled !== false,
    storageKey: "recentlyViewedProducts",
    maxProducts: 4,
    minProductsToShow: 4,
    freeShippingThresholdCents: resolveFreeShippingThresholdCents(
      THEME_CONFIG.freeShippingThresholdCents
    ),
    freeShippingPostGoalMessage:
      typeof THEME_CONFIG.freeShippingPostGoalMessage === "string"
        ? THEME_CONFIG.freeShippingPostGoalMessage
        : "You're eligible for <strong>FREE SHIPPING</strong>",
    // Frequency caps
    sessionCapKey: "bbExitRvModalShown",
    // Desktop exit intent
    desktopEnabled: true,
    // Mobile fallback
    mobileEnabled: true,
    mobileMinSecondsOnPage: 30,
    mobileMinScrollPercent: 50,
    // Safety
    excludePaths: ["/cart", "/checkout"],
  };

  /** Laptop/desktop breakpoint — explicit columns (auto-fit + shrink-wrap modal stayed 1 col wide). */
  const GRID_DESKTOP_MIN_WIDTH = 750;
  const GRID_DESKTOP_COL_PX = 200;

  function applyGridLayout(grid, modal, itemCount) {
    if (!grid || itemCount < 1) return;
    const desktop = window.innerWidth >= GRID_DESKTOP_MIN_WIDTH;
    if (desktop) {
      const cols = Math.min(4, Math.max(1, itemCount));
      grid.style.gridTemplateColumns = `repeat(${cols}, minmax(0, ${GRID_DESKTOP_COL_PX}px))`;
      grid.style.width = "auto";
      grid.style.maxWidth = "100%";
      grid.style.justifyContent = "center";
    } else {
      const cols = itemCount <= 1 ? 1 : Math.min(2, itemCount);
      grid.style.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`;
      grid.style.width = "100%";
      grid.style.maxWidth = "100%";
      grid.style.justifyContent = "center";
    }
    if (modal) {
      modal.style.width = "auto";
      modal.style.maxWidth = "min(980px, calc(100vw - 32px))";
    }
  }

  function isExcludedPath(pathname) {
    return CONFIG.excludePaths.some((p) => pathname === p || pathname.startsWith(p + "/"));
  }

  function isLikelyMobile() {
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) return true;
    return window.innerWidth < 990;
  }

  function safeParseJson(value, fallback) {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }

  function getViewedProductCount() {
    const raw = localStorage.getItem(CONFIG.storageKey) || "[]";
    const items = safeParseJson(raw, []);
    if (!Array.isArray(items)) return 0;

    const seen = new Set();
    for (const item of items) {
      const handle = item && item.handle;
      if (!handle || typeof handle !== "string") continue;
      seen.add(handle);
    }
    return seen.size;
  }

  function getRecentHandles() {
    const raw = localStorage.getItem(CONFIG.storageKey) || "[]";
    const items = safeParseJson(raw, []);
    if (!Array.isArray(items)) return [];

    const currentHandleMatch = window.location.pathname.match(/\/products\/([^\/]+)/);
    const currentHandle = currentHandleMatch ? currentHandleMatch[1] : null;

    const seen = new Set();
    const handles = [];
    for (const item of items) {
      const handle = item && item.handle;
      if (!handle || typeof handle !== "string") continue;
      if (currentHandle && handle === currentHandle) continue;
      if (seen.has(handle)) continue;
      seen.add(handle);
      handles.push(handle);
      if (handles.length >= CONFIG.maxProducts) break;
    }
    return handles;
  }

  function hasBeenShownThisSession() {
    try {
      return sessionStorage.getItem(CONFIG.sessionCapKey) === "1";
    } catch {
      return false;
    }
  }

  function markShownThisSession() {
    try {
      sessionStorage.setItem(CONFIG.sessionCapKey, "1");
    } catch {}
  }

  function ensureStyles() {
    if (document.getElementById("bb-exit-rv-modal-style")) return;
    const style = document.createElement("style");
    style.id = "bb-exit-rv-modal-style";
    style.textContent = `
      .bb-exit-rv-backdrop{
        position:fixed; inset:0; background:rgba(0,0,0,.45);
        display:flex; align-items:center; justify-content:center;
        z-index: 2147483000;
        padding: 16px;
      }
      .bb-exit-rv-modal{
        width: auto;
        max-width: min(980px, calc(100vw - 32px));
        background:#fff; color:#111;
        border-radius: 12px;
        box-shadow: 0 20px 60px rgba(0,0,0,.35);
        overflow:hidden;
        max-height: min(85vh, 720px);
        display:flex; flex-direction:column;
      }
      .bb-exit-rv-header{
        display:flex; align-items:flex-start; justify-content:space-between;
        gap: 12px;
        padding: 16px 16px 0 16px;
        width: 100%;
        box-sizing: border-box;
        min-width: 0;
      }
      .bb-exit-rv-title{
        font-size: 18px; font-weight: 700; margin: 0;
      }
      .bb-exit-rv-subtitle{
        font-size: 14px; margin: 6px 0 0 0; color: rgba(0,0,0,.65);
      }
      .bb-exit-rv-free-ship{
        margin-top: 10px;
        padding: 10px 12px;
        border-radius: 10px;
        background: rgba(249, 193, 198, .22);
        border: 1px solid rgba(0,0,0,.08);
        font-size: 13px;
        color: rgba(0,0,0,.85);
      }
      .bb-exit-rv-close{
        appearance:none; border:0; background:transparent; cursor:pointer;
        width: 40px; height: 40px; border-radius: 10px;
        display:grid; place-items:center;
      }
      .bb-exit-rv-close:hover{ background: rgba(0,0,0,.06); }
      .bb-exit-rv-body{
        padding: 12px 16px 16px 16px;
        overflow:auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        box-sizing: border-box;
        width: 100%;
      }
      /* Column count set in JS so laptop/desktop gets 2–4 columns (auto-fit + max-content only ever showed 1 col) */
      .bb-exit-rv-grid{
        display: grid;
        gap: 12px;
        justify-content: center;
        justify-items: stretch;
        width: 100%;
        max-width: 100%;
      }
      .bb-exit-rv-grid > div.bb-exit-rv-card-cell {
        min-width: 0;
      }
      /* Fill ratio box so inactive slides don’t leave a tall grey gap below the image */
      .bb-exit-rv-modal .card__inner.ratio {
        position: relative;
      }
      .bb-exit-rv-modal .card__inner.ratio .card__media {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
      }
      .bb-exit-rv-modal .card__media-carousel,
      .bb-exit-rv-modal .card__media-carousel-track {
        height: 100%;
      }
      @media (min-width: 768px){
        .bb-exit-rv-header{ padding: 20px 20px 0 20px; }
        .bb-exit-rv-body{ padding: 14px 20px 20px 20px; }
      }
      .bb-exit-rv-actions{
        display:flex; gap: 10px; flex-wrap:wrap;
        margin-top: 14px;
        justify-content: center;
        align-self: stretch;
        width: 100%;
        box-sizing: border-box;
      }
      .bb-exit-rv-btn{
        appearance:none; border:1px solid rgba(0,0,0,.18);
        background:#fff; color:#111;
        border-radius: 10px;
        padding: 10px 12px;
        font-size: 14px;
        cursor:pointer;
      }
      .bb-exit-rv-btn--primary{
        background:#111; color:#fff; border-color:#111;
      }
    `;
    document.head.appendChild(style);
  }

  function buildModalShell() {
    const backdrop = document.createElement("div");
    backdrop.className = "bb-exit-rv-backdrop";
    backdrop.setAttribute("role", "presentation");

    const modal = document.createElement("div");
    modal.className = "bb-exit-rv-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-label", "Recently viewed products");

    modal.innerHTML = `
      <div class="bb-exit-rv-header">
        <div>
          <p class="bb-exit-rv-title">Still deciding?</p>
          <p class="bb-exit-rv-subtitle">Here are items you looked at recently.</p>
          <div class="bb-exit-rv-free-ship" hidden></div>
        </div>
        <button type="button" class="bb-exit-rv-close" aria-label="Close">
          <span aria-hidden="true">✕</span>
        </button>
      </div>
      <div class="bb-exit-rv-body">
        <div class="bb-exit-rv-grid" aria-live="polite"></div>
        <div class="bb-exit-rv-actions">
          <button type="button" class="bb-exit-rv-btn bb-exit-rv-btn--primary" data-action="continue">Continue shopping</button>
          <button type="button" class="bb-exit-rv-btn" data-action="cart">View cart</button>
        </div>
      </div>
    `;

    backdrop.appendChild(modal);
    return { backdrop, modal, grid: modal.querySelector(".bb-exit-rv-grid") };
  }

  async function fetchCartTotalCents() {
    try {
      const r = await fetch("/cart.js", { credentials: "same-origin" });
      if (!r.ok) return null;
      const data = await r.json();
      const total = data && typeof data.total_price === "number" ? data.total_price : null;
      return total;
    } catch {
      return null;
    }
  }

  function maybeShowFreeShippingMessage(modal, cartTotalCents) {
    if (!modal) return;
    if (!CONFIG.freeShippingThresholdCents || typeof cartTotalCents !== "number") return;
    if (cartTotalCents < CONFIG.freeShippingThresholdCents) return;

    const el = modal.querySelector(".bb-exit-rv-free-ship");
    if (!el) return;
    el.hidden = false;
    el.innerHTML = CONFIG.freeShippingPostGoalMessage;
  }

  async function fetchCardHtml(handle) {
    const url = `/products/${encodeURIComponent(handle)}?view=card-only`;
    try {
      const r = await fetch(url, { credentials: "same-origin" });
      if (!r.ok) return null;
      const html = await r.text();
      if (!html || !html.trim()) return null;
      return html;
    } catch {
      return null;
    }
  }

  async function renderCards(grid, modal, handles) {
    const cards = await Promise.all(handles.map(fetchCardHtml));
    const valid = cards.filter(Boolean);
    if (!valid.length) return false;

    const frag = document.createDocumentFragment();
    for (const card of valid) {
      const wrapper = document.createElement("div");
      wrapper.className = "bb-exit-rv-card-cell";
      wrapper.innerHTML = card;
      frag.appendChild(wrapper);
    }
    grid.appendChild(frag);
    applyGridLayout(grid, modal, valid.length);
    return true;
  }

  function openModal() {
    if (isExcludedPath(window.location.pathname)) return false;
    if (hasBeenShownThisSession()) return false;

    const handles = getRecentHandles();
    if (getViewedProductCount() < CONFIG.minProductsToShow) return false;
    if (!handles.length) return false;

    ensureStyles();
    const { backdrop, modal, grid } = buildModalShell();
    if (!grid) return false;

    const previousActive = document.activeElement;
    let resizeHandler = null;

    function close() {
      if (resizeHandler) {
        window.removeEventListener("resize", resizeHandler);
        resizeHandler = null;
      }
      document.removeEventListener("keydown", onKeyDown, true);
      backdrop.remove();
      document.body.classList.remove("overflow-hidden");
      if (previousActive && typeof previousActive.focus === "function") previousActive.focus();
    }

    function onKeyDown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    }

    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) close();
    });

    modal.querySelector(".bb-exit-rv-close")?.addEventListener("click", close);
    modal.querySelector('[data-action="continue"]')?.addEventListener("click", close);
    modal.querySelector('[data-action="cart"]')?.addEventListener("click", () => {
      window.location.href = "/cart";
    });

    document.addEventListener("keydown", onKeyDown, true);
    document.body.appendChild(backdrop);
    document.body.classList.add("overflow-hidden");

    // Focus close button for accessibility
    modal.querySelector(".bb-exit-rv-close")?.focus?.();

    // Render cards async
    renderCards(grid, modal, handles).then((ok) => {
      if (!ok) {
        close();
        return;
      }
      if (!document.body.contains(backdrop)) return;
      resizeHandler = () => {
        if (!document.body.contains(backdrop)) return;
        applyGridLayout(grid, modal, grid.children.length);
      };
      window.addEventListener("resize", resizeHandler);
    });

    // Show free shipping message if eligible
    fetchCartTotalCents().then((totalCents) => {
      maybeShowFreeShippingMessage(modal, totalCents);
    });

    markShownThisSession();
    return true;
  }

  function setupDesktopExitIntent() {
    let armed = true;
    const handler = (e) => {
      if (!armed) return;
      if (e.relatedTarget !== null) return;
      if (typeof e.clientY === "number" && e.clientY > 0) return;
      if (openModal()) armed = false;
    };
    document.addEventListener("mouseout", handler, { passive: true });
  }

  function setupMobileFallback() {
    const startTs = Date.now();
    let opened = false;

    const getScrollPercent = () => {
      const doc = document.documentElement;
      const body = document.body;
      const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
      const scrollHeight = Math.max(body.scrollHeight, doc.scrollHeight);
      const clientHeight = doc.clientHeight || window.innerHeight || 1;
      const maxScroll = Math.max(1, scrollHeight - clientHeight);
      return Math.min(100, Math.max(0, (scrollTop / maxScroll) * 100));
    };

    const maybeOpen = () => {
      if (opened) return;
      const seconds = (Date.now() - startTs) / 1000;
      if (seconds < CONFIG.mobileMinSecondsOnPage) return;
      if (getScrollPercent() < CONFIG.mobileMinScrollPercent) return;
      if (openModal()) opened = true;
    };

    window.addEventListener("scroll", maybeOpen, { passive: true });
    const interval = setInterval(() => {
      maybeOpen();
      if (opened || hasBeenShownThisSession()) clearInterval(interval);
    }, 1000);
  }

  function init() {
    if (!CONFIG.enabled) return;
    if (isExcludedPath(window.location.pathname)) return;
    if (hasBeenShownThisSession()) return;

    if (CONFIG.desktopEnabled) setupDesktopExitIntent();
    if (CONFIG.mobileEnabled && isLikelyMobile()) setupMobileFallback();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();